import { useRef, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { TemplateBuilderInput, TemplateBuilderInputHandle } from './template-builder-input';
import { useCreatePromptTemplate } from '@/api/mutations/useCreatePromptTemplate';
import { Icon } from '@/new/components/icon';
import { PageTitle } from '@/new/components/page-title';
import { Button } from '@/new/components/shadcn/button';
import { Input } from '@/new/components/shadcn/input';
import { Label } from '@/new/components/shadcn/label';

export function KimTemplateBuilder() {
  const { matterId, subsetId } = useParams();
  const navigate = useNavigate();
  const [label, setLabel] = useState('');
  const [body, setBody] = useState('');
  const inputRef = useRef<TemplateBuilderInputHandle>(null);
  const { mutate: createTemplate, isLoading } = useCreatePromptTemplate();

  const handleCreate = () => {
    const title = label.trim();
    const trimmedBody = body.trim();
    if (!title || !trimmedBody) return;

    createTemplate(
      { title, body: trimmedBody },
      {
        onSuccess: () => {
          navigate(`/matters/${matterId}/subsets/${subsetId}/new-kim`);
        },
        onError: (error) => {
          toast.error(error.message || 'Error creating template. Please try again.');
        },
      },
    );
  };

  return (
    <div className="h-full w-full overflow-y-auto">
      <div className="mx-auto max-w-5xl px-8 py-5 pb-32">
        <div className="my-12">
          <PageTitle text="Templates" />
        </div>

        <div className="flex w-full flex-col">
          <div className="flex h-64 w-full flex-col">
            <div className="mb-1 flex justify-between p-2 pb-1.5">
              <Label className="mb-0">Template Content</Label>

              <div className="flex items-center gap-1.75 text-xs text-gray-500">
                <Icon className="lucide--info mb-px" />
                Press <kbd className="text-[11px]">[</kbd> to insert a placeholder
              </div>
            </div>

            <TemplateBuilderInput
              ref={inputRef}
              value={body}
              onChange={setBody}
              placeholder="Build a timeline of events, structured as a table with columns for..."
              className="w-full grow"
              autoFocus
            />
          </div>

          <div className="mt-7 w-full">
            <Label htmlFor="template-name" className="mb-3 pl-2">
              Name
            </Label>
            <div className="flex gap-2">
              <Input
                id="template-name"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g. Structured timeline"
              />
              <Button
                variant="primary"
                className="gap-1.5 pl-3"
                disabled={!label.trim() || !body.trim() || isLoading}
                onClick={handleCreate}
              >
                Create template <Icon className="lucide--chevron-right" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
